'use client';

import { memo as Memo, useMemo, useState, useCallback, useRef, useEffect, Fragment } from 'react';
import { useRouter } from 'next/navigation';
import { TweenMax } from 'gsap';

//* Api
import api from '@/utils/api';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';

//* JSONs
import formsJson from '@lib/forms.json';

//* Components
import Form from '@components/global/Form';
import Text from '@components/global/Text';
import Page from '@components/global/Page';
import Translate from '@components/global/Translate';
import HTMLContainer from '@components/global/HTMLContainer';
import DarkModeSwitcher from '@components/global/DarkModeSwitcher';
import OurSocialContacts from '@components/global/OurSocialContacts';
import NextPreSteps from '@components/pages/ContactForm/NextPreSteps';
import ContactFormStep from '@components/pages/ContactForm/ContactFormStep';
import SharpyThankYou from '@components/global/SvgComponents/SharpyThankYou';

//* Styles
import ContactFormStyle from '@/styles/pages/ContactFormStyle';

const ContactForm = Memo(({ winWidth, screenSizes }) => {
	//! Router
	const router = useRouter();

	//! Default State
	const defaultState = useMemo(() => ({ formDatas: {}, activeStep: 0 }), []);

	//! Refs
	let formRefs = useRef([]);
	let formRefsStep = useRef([]);
	let titleRef = useRef([]);
	let refLink = useRef();
	let refAnime = useRef();
	let timeout1 = useRef();
	let timeout2 = useRef();
	let timeout3 = useRef();
	let timeout4 = useRef();

	//! States
	const [state, setState] = useState(defaultState);
	const [loading, setLoading] = useState(false);

	//! Prev Step
	const prevStep = useCallback(() => {
		setState({ ...state, activeStep: --state.activeStep });
	}, [state]);

	//! Form Submit
	const submit = useCallback(
		(data, k) => {
			if (k === 4) {
				setLoading(true);

				const newData = { ...state.formDatas, [k]: data };
				const dataArr = Object.values(newData);

				let formData = {};

				for (let i = 0; i < dataArr.length; i++) {
					formData = { ...formData, ...dataArr[i] };
				}

				api.post('contact-us', formData).then(() => {
					setLoading(false);
					router.push('/thank-you');
				});
			} else {
				setState({
					formDatas: { ...state.formDatas, [k]: data },
					activeStep: k + 1,
				});
			}
		},
		[state, loading]
	);

	//! Update Form Values
	const updateFormValues = useCallback(
		(form, index) => {
			return form;
		},
		[state]
	);

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'auto',
		});

		timeout1.current = setTimeout(() => {
			TweenMax.set(titleRef.current[state.activeStep], {
				opacity: 1,
			});
		}, 500);

		state.activeStep !== formsJson.forms.length - 1 &&
			formRefs.current[state.activeStep]?.rowRefs &&
			(timeout2.current = setTimeout(() => {
				TweenMax.staggerTo(formRefs.current[state.activeStep].rowRefs, 0, {
					opacity: 1,
				});

				TweenMax.staggerTo(formRefs.current[state.activeStep].rowRefs[0].childNodes[0].children, 0, {
					opacity: 1,
				});
			}, 800));

		for (let i = 0; i <= formRefsStep.current.length; i++) {
			if (state.activeStep === i) {
				TweenMax.to(
					formRefsStep.current[i],
					0,
					{
						opacity: 1,
					},
					1
				);
			} else {
				TweenMax.set(formRefsStep.current[i], {
					opacity: 0,
				});

				TweenMax.set(titleRef.current[i], {
					opacity: 0,
				});

				TweenMax.staggerTo(refAnime.current, 0, {
					opacity: 0,
				});

				refLink.current?.children &&
					TweenMax.staggerTo(refLink.current.children, 0, {
						opacity: 0,
					});
			}

			if (state.activeStep === formRefsStep.current.length - 1) {
				timeout3.current = setTimeout(() => {
					TweenMax.staggerTo(refAnime.current, 0, {
						opacity: 1,
					});
				}, 700);

				timeout4.current = setTimeout(() => {
					TweenMax.staggerTo(
						refLink.current.children,
						0,
						{
							opacity: 1,
						},
						0.1
					);
				}, 900);
			}
		}

		return () => {
			timeout1.current && clearTimeout(timeout1.current);
			timeout2.current && clearTimeout(timeout2.current);
			timeout3.current && clearTimeout(timeout3.current);
			timeout4.current && clearTimeout(timeout4.current);
		};
	}, [state, formRefsStep]);

	//! Steps
	const forms = useMemo(() => {
		return formsJson.forms.map((f, k) => {
			return (
				<div
					key={k}
					ref={(el) => (formRefsStep.current[k] = el)}
					className={`step ${k === state.activeStep ? 'activeStep' : ''}`}>
					{state.activeStep < formsJson.forms.length && (
						<Fragment>
							<Text
								text={f.title}
								tag={k === 0 ? 'h2' : 'p'}
								ref={(el) => (titleRef.current[k] = el)}
								className={`formTitle font-light font-poppins`}
							/>

							{f.form ? (
								<Form
									className={'inputClass'}
									wrapContClass={'inputWrapClass'}
									submit={(data) => submit(data, k)}
									ref={(el) => (formRefs.current[k] = el)}
									{...updateFormValues(f.form, k)}
								/>
							) : (
								<div className={'thankYouWrap'}>
									<div
										ref={refAnime}
										className={'thankYouAnime'}>
										<SharpyThankYou active={state.activeStep === formsJson.forms.length - 1} />
									</div>

									<div
										ref={refLink}
										className={'OtherContact'}>
										<Text
											text={f.subtitle}
											className={`OtherContactSubTitle h6 font-light font-poppins`}
										/>

										<OurSocialContacts
											minType
											animate={state}
										/>
									</div>
								</div>
							)}
						</Fragment>
					)}
				</div>
			);
		});
	}, [formsJson, formRefs, state, loading]);

	const stepChange = (ind) => {
		Object.keys(state.formDatas).length > ind && setState({ ...state, activeStep: ind });
	};

	const nextStepGo = () => {
		formRefs.current[state.activeStep].submitForm();
	};

	const closeBtn = useCallback(
		(isMobile = false) => {
			return (winWidth >= screenSizes.screenS && !isMobile) || (winWidth < screenSizes.screenS && isMobile) ? (
				<span
					onClick={() => router.back()}
					className={`backLink font-bold font-anonymous h6 crCircle`}>
					<Translate val={'back'} />
				</span>
			) : null;
		},
		[winWidth, screenSizes]
	);

	return (
		<Page className={`contactForm`}>
			<ContactFormStyle>
				{closeBtn()}

				<HTMLContainer>
					<div className='section-item'>
						<div className={'OtherFunctionality'}>
							<div className={'topSection'}>
								{closeBtn(true)}

								<DarkModeSwitcher />
							</div>

							<ContactFormStep
								form={formsJson.forms}
								onClick={stepChange}
								active={state.activeStep}
							/>
						</div>

						<div className={`stepsContainer ${loading ? 'loading' : ''}`}>
							{forms}

							{state.activeStep !== formsJson.forms.length - 1 && (
								<NextPreSteps
									stateActive={state.activeStep}
									prevStepOnclick={prevStep}
									nextStepOnclick={nextStepGo}
								/>
							)}
						</div>
					</div>
				</HTMLContainer>
			</ContactFormStyle>
		</Page>
	);
});

export default withUIContext(ContactForm, ['winWidth', 'screenSizes']);
